import type { StudioBranch } from "@certa/queries/types/studio.types";

export enum StudioEnvBranchName {
  DEVELOPMENT = "main",
  QA = "ds__env__certaqa",
  STAGING = "ds__env__certasite",
  PRODUCTION = "ds__env__production"
}

export const StudioEnvLabels: Record<StudioEnvBranchName, { label: string }> = {
  [StudioEnvBranchName.DEVELOPMENT]: {
    label: "Development"
  },
  [StudioEnvBranchName.QA]: {
    label: "QA"
  },
  [StudioEnvBranchName.STAGING]: {
    label: "Staging"
  },
  [StudioEnvBranchName.PRODUCTION]: {
    label: "Live"
  }
};

export const StudioEnvDisplayOrder: StudioEnvBranchName[] = [
  StudioEnvBranchName.DEVELOPMENT,
  StudioEnvBranchName.QA,
  StudioEnvBranchName.STAGING,
  StudioEnvBranchName.PRODUCTION
];

export const isReleaseBranch = (branchName: string): boolean => {
  const standardBranches = Object.values(StudioEnvBranchName);
  return standardBranches.includes(branchName as StudioEnvBranchName);
};

// Helper to determine if a branch is a draft branch
// Draft branches have baseSavepoint.branchName === rootBranch (defaults to DEVELOPMENT for backward compatibility)
export const isDraftBranch = (
  branch: StudioBranch,
  rootBranch: StudioEnvBranchName
): boolean => {
  return (
    !isReleaseBranch(branch.name) &&
    branch.baseSavepoint?.branchName === rootBranch
  );
};

// Helper to determine if a branch is a hotfix branch
// Hotfix branches have baseSavepoint.branchName === PRODUCTION
export const isHotfixBranch = (branch: StudioBranch): boolean => {
  return (
    !isReleaseBranch(branch.name) &&
    branch.baseSavepoint?.branchName === StudioEnvBranchName.PRODUCTION
  );
};

// Helper to check if a branch is one of the first 3 release branches (Dev, QA, Staging)
// These branches should allow data edits even when UI appears readonly
export const isNonProductionReleaseBranch = (branchName: string): boolean => {
  return [
    StudioEnvBranchName.DEVELOPMENT,
    StudioEnvBranchName.QA,
    StudioEnvBranchName.STAGING
  ].includes(branchName as StudioEnvBranchName);
};

// Mapping from StudioEnvBranchName to OS3 ATD ValidEnvs
// This is used to check migration status before allowing deployments
export const BranchNameToValidEnvsMap: Record<
  StudioEnvBranchName,
  "slackcart" | "certaqa" | "certasite" | "production"
> = {
  [StudioEnvBranchName.DEVELOPMENT]: "slackcart",
  [StudioEnvBranchName.QA]: "certaqa",
  [StudioEnvBranchName.STAGING]: "certasite",
  [StudioEnvBranchName.PRODUCTION]: "production"
};

export const isBranchInactive = (branch: StudioBranch | undefined): boolean => {
  if (!branch) return false;
  return (
    branch.isFrozen ||
    branch.status === "frozen" ||
    branch.status === "archived"
  );
};
